<template>
  <div class="p-4">
    <div class="max-w-5xl mx-auto relative">
      <h1 class="text-2xl font-semibold mb-4">{{ pageTitle }}</h1>
      <div class="text-xs text-gray-500 dark:text-gray-400 mb-6">{{ lastUpdatedAll }}</div>
      <div class="flex">
        <div class="flex-1" :class="isRtl === true ? 'pl-6' : 'pr-6'">
      <div id="terms" class="mb-12">
        <h2 class="text-xl font-semibold mb-3">{{ termsTitle }}</h2>
        <div class="prose prose-sm dark:prose-invert max-w-none" :class="isRtl === true ? 'text-right' : 'text-left'" v-html="termsHtml"></div>
      </div>
      <div class="h-px bg-gray-200 dark:bg-gray-700 my-10"></div>
      <div id="privacy" class="mb-12">
        <h2 class="text-xl font-semibold mb-3">{{ privacyTitle }}</h2>
        <div class="prose prose-sm dark:prose-invert max-w-none" :class="isRtl === true ? 'text-right' : 'text-left'" v-html="privacyHtml"></div>
      </div>
      <div class="h-px bg-gray-200 dark:bg-gray-700 my-10"></div>
      <div id="cookies" class="mb-12">
        <h2 class="text-xl font-semibold mb-3">{{ cookiesTitle }}</h2>
        <div class="prose prose-sm dark:prose-invert max-w-none" :class="isRtl === true ? 'text-right' : 'text-left'" v-html="cookiesHtml"></div>
      </div>
      <div class="h-px bg-gray-200 dark:bg-gray-700 my-10"></div>
      <div id="dpa" class="mb-12">
        <h2 class="text-xl font-semibold mb-3">{{ dpaTitle }}</h2>
        <div class="prose prose-sm dark:prose-invert max-w-none" :class="isRtl === true ? 'text-right' : 'text-left'" v-html="dpaHtml"></div>
      </div>
        </div>
        <nav class="hidden md:block w-56" :class="isRtl === true ? 'mr-6' : 'ml-6'" style="position: sticky; top: 96px; align-self: flex-start;">
          <div class="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-md p-2 shadow">
            <ul class="flex flex-col">
              <li class="mb-2"><a class="text-sm text-blue-600 hover:underline" :class="activeClass('#terms')" :href="'/termsAndConditions#terms'" @click.prevent="navigateTo('#terms')">{{ termsNav }}</a></li>
              <li class="mb-2"><a class="text-sm text-blue-600 hover:underline" :class="activeClass('#privacy')" :href="'/termsAndConditions#privacy'" @click.prevent="navigateTo('#privacy')">{{ privacyNav }}</a></li>
              <li class="mb-2"><a class="text-sm text-blue-600 hover:underline" :class="activeClass('#cookies')" :href="'/termsAndConditions#cookies'" @click.prevent="navigateTo('#cookies')">{{ cookiesNav }}</a></li>
              <li><a class="text-sm text-blue-600 hover:underline" :class="activeClass('#dpa')" :href="'/termsAndConditions#dpa'" @click.prevent="navigateTo('#dpa')">{{ dpaNav }}</a></li>
            </ul>
          </div>
        </nav>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { marked } from 'marked';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from '@/i18n';
import { globalState } from '@/globalState';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';

const renderer = new marked.Renderer();
marked.setOptions({
  renderer,
  gfm: true,
  breaks: true,
});

const route = useRoute();
const router = useRouter();
const { currentLanguage } = useI18n();
const isRtl = computed(() => {
  try {
    const fromI18n = (currentLanguage?.value !== undefined ? currentLanguage.value : '').toString().toLowerCase();
    if (fromI18n === 'he' || fromI18n === 'ar' || (typeof fromI18n === 'string' && (fromI18n.startsWith('he') === true || fromI18n.startsWith('ar') === true))) {
      return true;
    }
    const fromGlobal = (globalState?.orgLanguage !== undefined ? globalState.orgLanguage : '').toString().toLowerCase();
    if (fromGlobal === 'he' || fromGlobal === 'ar' || fromGlobal.startsWith('he') === true || fromGlobal.startsWith('ar') === true) {
      return true;
    }
    const htmlLang = (document?.documentElement?.lang !== undefined ? document.documentElement.lang : '').toString().toLowerCase();
    if (htmlLang === 'he' || htmlLang === 'ar' || htmlLang.startsWith('he') === true || htmlLang.startsWith('ar') === true) {
      return true;
    }
    const navLang = (navigator?.language !== undefined ? navigator.language : '').toString().toLowerCase();
    if (navLang === 'he' || navLang === 'ar' || navLang.startsWith('he') === true || navLang.startsWith('ar') === true) {
      return true;
    }
  } catch (error) {
  }
  return false;
});
function getLang(){
  try {
    const fromI18n = (currentLanguage?.value !== undefined ? currentLanguage.value : '').toString().toLowerCase();
    if (fromI18n === 'he' || fromI18n === 'en' || fromI18n === 'es' || fromI18n === 'it' || fromI18n === 'ar') {
      return fromI18n;
    }
    if (typeof fromI18n === 'string' && fromI18n.length >= 2) {
      const short = fromI18n.substring(0, 2);
      if (short === 'he' || short === 'en' || short === 'es' || short === 'it' || short === 'ar') {
        return short;
      }
    }
    const fromGlobal = (globalState?.orgLanguage !== undefined ? globalState.orgLanguage : '').toString().toLowerCase();
    if (fromGlobal === 'he' || fromGlobal === 'en' || fromGlobal === 'es' || fromGlobal === 'it' || fromGlobal === 'ar') {
      return fromGlobal;
    }
    if (typeof fromGlobal === 'string' && fromGlobal.length >= 2) {
      const short2 = fromGlobal.substring(0, 2);
      if (short2 === 'he' || short2 === 'en' || short2 === 'es' || short2 === 'it' || short2 === 'ar') {
        return short2;
      }
    }
    const htmlLang = (document?.documentElement?.lang !== undefined ? document.documentElement.lang : '').toString().toLowerCase();
    if (htmlLang === 'he' || htmlLang === 'en' || htmlLang === 'es' || htmlLang === 'it' || htmlLang === 'ar') {
      return htmlLang;
    }
    if (typeof htmlLang === 'string' && htmlLang.length >= 2) {
      const short3 = htmlLang.substring(0, 2);
      if (short3 === 'he' || short3 === 'en' || short3 === 'es' || short3 === 'it' || short3 === 'ar') {
        return short3;
      }
    }
    const navLang = (navigator?.language !== undefined ? navigator.language : '').toString().toLowerCase();
    if (navLang === 'he' || navLang === 'en' || navLang === 'es' || navLang === 'it' || navLang === 'ar') {
      return navLang;
    }
    if (typeof navLang === 'string' && navLang.length >= 2) {
      const short4 = navLang.substring(0, 2);
      if (short4 === 'he' || short4 === 'en' || short4 === 'es' || short4 === 'it' || short4 === 'ar') {
        return short4;
      }
    }
  } catch (error) {
  }
  return 'en';
}
function langShort(){
  const l = getLang();
  if (typeof l === 'string' && l.length >= 2) { return l.substring(0,2); }
  return 'en';
}
const pageTitle = computed(() => {
  const l = langShort();
  if (l === 'he') { return 'תנאים ומדיניות'; }
  if (l === 'ar') { return 'الشروط والسياسات'; }
  if (l === 'es') { return 'Términos y políticas'; }
  if (l === 'it') { return 'Termini e politiche'; }
  return 'Terms and Policies';
});
const lastUpdatedAll = computed(() => {
  const l = langShort();
  if (l === 'he') { return 'עודכן לאחרונה: 28 אוג׳ 2025'; }
  if (l === 'ar') { return 'آخر تحديث: 28 آب 2025'; }
  if (l === 'es') { return 'Última actualización: 28 Ago 2025'; }
  if (l === 'it') { return 'Ultimo aggiornamento: 28 Ago 2025'; }
  return 'Last updated: 28 Aug 2025';
});
const termsTitle = computed(() => {
  const l = langShort();
  if (l === 'he') { return 'תנאי שימוש'; }
  if (l === 'ar') { return 'شروط الاستخدام'; }
  if (l === 'es') { return 'Términos de uso'; }
  if (l === 'it') { return 'Termini di utilizzo'; }
  return 'Terms of Use';
});
const privacyTitle = computed(() => {
  const l = langShort();
  if (l === 'he') { return 'מדיניות פרטיות'; }
  if (l === 'ar') { return 'سياسة الخصوصية'; }
  if (l === 'es') { return 'Política de privacidad'; }
  if (l === 'it') { return 'Informativa sulla privacy'; }
  return 'Privacy Policy';
});
const cookiesTitle = computed(() => {
  const l = langShort();
  if (l === 'he') { return 'מדיניות עוגיות'; }
  if (l === 'ar') { return 'سياسة ملفات تعريف الارتباط'; }
  if (l === 'es') { return 'Política de cookies'; }
  if (l === 'it') { return 'Cookie Policy'; }
  return 'Cookie Policy';
});
const dpaTitle = computed(() => {
  const l = langShort();
  if (l === 'he') { return 'נספח עיבוד נתונים (DPA)'; }
  if (l === 'ar') { return 'اتفاقية معالجة البيانات (DPA)'; }
  if (l === 'es') { return 'Anexo de Tratamiento de Datos (DPA)'; }
  if (l === 'it') { return 'Addendum sul trattamento dei dati (DPA)'; }
  return 'Data Processing Addendum (DPA)';
});
const termsNav = termsTitle;
const privacyNav = privacyTitle;
const cookiesNav = cookiesTitle;
const dpaNav = dpaTitle;
const termsHtml = ref('');
const privacyHtml = ref('');
const cookiesHtml = ref('');
const dpaHtml = ref('');
const currentSection = ref('#terms');
const isProgrammaticScroll = ref(false);
async function loadOne(prefix){
  const lang = getLang();
  const cands = [`/policies/${prefix}.${lang}.md`,`/policies/${prefix}.${lang.substring(0,2)}.md`,`/policies/${prefix}.en.md`];
  for (let i = 0; i < cands.length; i = i + 1) {
    try {
      const r = await fetch(cands[i]);
      if (r?.ok === true) {
        const t = await r.text();
        return t;
      }
    } catch (e) {
    }
  }
  return '';
}
async function loadAll(){
  const [t,p,c,d] = await Promise.all([loadOne('terms'), loadOne('privacy'), loadOne('cookies'), loadOne('dpa')]);
  termsHtml.value = sanitizeHtml(marked.parse(t));
  privacyHtml.value = sanitizeHtml(marked.parse(p));
  cookiesHtml.value = sanitizeHtml(marked.parse(c));
  dpaHtml.value = sanitizeHtml(marked.parse(d));
}
function scrollToHash(){
  const h = route.hash;
  if (typeof h === 'string' && h.length > 1) {
    requestAnimationFrame(() => {
      const el = document.querySelector(h);
      if (el !== undefined && el !== null) {
        isProgrammaticScroll.value = true;
        el.scrollIntoView({ behavior: 'smooth' });
        window.setTimeout(() => { isProgrammaticScroll.value = false; }, 600);
      }
    });
  }
}
async function navigateTo(h){
  if (typeof h === 'string' && h.length > 1) {
    isProgrammaticScroll.value = true;
    const samePage = route.path === '/termsAndConditions';
    const sameHash = route.hash === h;
    try {
      if (samePage === true && sameHash === true) {
      } else if (samePage === true) {
        await router.replace({ path: '/termsAndConditions', hash: h });
      } else {
        await router.push({ path: '/termsAndConditions', hash: h });
      }
    } catch (e) {
    } finally {
      currentSection.value = h;
      requestAnimationFrame(() => {
        const el = document.querySelector(h);
        if (el !== undefined && el !== null) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
        window.setTimeout(() => { isProgrammaticScroll.value = false; }, 700);
      });
    }
  }
}
function activeClass(hash){
  if (currentSection.value === hash) { return 'font-semibold'; }
  return '';
}
let observer;
onMounted(async () => {
  await loadAll();
  scrollToHash();
  const sections = ['#terms','#privacy','#cookies','#dpa'];
  const opts = { root: null, rootMargin: '0px', threshold: [0.25,0.5,0.75] };
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const id = '#' + entry.target.id;
      if (entry.isIntersecting === true && entry.intersectionRatio >= 0.5) {
        if (isProgrammaticScroll.value === true) {
          return;
        }
        currentSection.value = id;
        if (route.path === '/termsAndConditions') {
          router.replace({ path: '/termsAndConditions', hash: id });
        }
      }
    });
  }, opts);
  sections.forEach((sel) => {
    const el = document.querySelector(sel);
    if (el !== undefined && el !== null) {
      observer.observe(el);
    }
  });
});
watch(() => route.hash, () => {
  if (isProgrammaticScroll.value === true) {
    return;
  }
  scrollToHash();
});
watch(() => currentLanguage?.value, async () => {
  await loadAll();
});
onUnmounted(() => {
  if (observer !== undefined && observer !== null) {
    observer.disconnect();
  }
});
</script>
<style scoped>
.prose {
  line-height: 1.6;
}
#terms, #privacy, #cookies, #dpa { scroll-margin-top: 110px; }
</style>
